// #Faz2 Tahsilat–Fatura Eşleştirme — paylaşılan sabitler, biçimleyiciler ve API çağrıları.
//
// Tüketiciler: UnallocatedCollectionsPage (kuyruk), AgingReportPage (yaşlandırma),
// FinanceTransactionDetailPage (İlişkili Faturalar), InvoiceDetailPage (İlişkili Tahsilatlar),
// AllocationModal (dağıtım / yeniden eşleştirme).
//
// KURALLAR (spec §10) — UI bunları ÖNDEN uygular, backend (InvoiceAllocationService) yine de
// son sözü söyler; iki taraf da aynı tavanı kullanır (bkz. effectiveOpen).
import { authHeaders } from '../listPageStyles';
import { listRouteFor } from './financeTypeMeta';

export const FT_API = '/api/financetransactions';
export const INVOICE_API = '/api/invoices';
export const CUSTOMER_API = '/api/customers';
export const REPRESENTATIVE_API = '/api/representatives';
export const AGING_API = '/api/reports/aging';

// FinanceTransaction.Direction / Status (FinanceTransactionsPage ile birebir).
export const DIRECTION_COLLECTION = 1;
export const STATUS_ACTIVE = 1;

// FinanceTransaction.Method → etiket.
export const METHOD_LABELS = {
  1: 'Nakit', 2: 'Kredi Kartı', 3: 'Çek', 4: 'Senet', 5: 'DBS', 6: 'Manuel Düzeltme',
};

/**
 * Bir cari hareketin detay ekranı rotası.
 *
 * Eskiden burada `method → slug` sabit bir tablo vardı ve iki kez bayatladı: Havale/EFT eklenince
 * eksik kaldı (havale tahsilatına tıklamak sessizce NAKİT ekranını açıyordu), #827'de de manuel
 * yöntemin grubu değişince yanlış yolu gösterecekti. Rota artık TÜR KAYIT DEFTERİNDEN çözülür
 * (financeTypeMeta) — yön + yöntem birlikte, tek kaynak.
 *
 * @returns {string|null} detay rotası ya da tür çözülemezse null (çağıran bağlantı vermemeli).
 */
export function transactionDetailRoute(direction, method, id) {
  const base = listRouteFor(direction, method);
  return base ? `${base}/${id}` : null;
}

// CollectionAllocation.AllocatedBy (AllocationSource): 1=MobileAuto, 2=MobileManual, 3=Backoffice.
export const ALLOCATION_SOURCE_LABELS = {
  1: 'Mobil (Otomatik)', 2: 'Mobil (Elle)', 3: 'Backoffice',
};

// Invoice.InvoiceStatus (COMPUTED PERSISTED): 0=open, 1=partially_paid, 2=paid, 3=overpaid.
// overpaid = negatif açık tutar → VERİ HATASI, kırmızı gösterilir (spec §10.10).
export const INVOICE_STATUS_META = {
  0: { label: 'Açık', tone: 'neutral' },
  1: { label: 'Kısmi Ödendi', tone: 'wait' },
  2: { label: 'Ödendi', tone: 'ok' },
  3: { label: 'Fazla Ödenmiş', tone: 'bad' },
};

export const invoiceStatusMeta = (v) => INVOICE_STATUS_META[Number(v)] || { label: '-', tone: 'neutral' };

// Yaşlandırma kovaları — backend ReportsController.BucketOf ile birebir (0=vadesi gelmemiş).
export const AGING_BUCKETS = [
  { key: 'notDue', bucket: 0, label: 'Vadesi Gelmemiş', totalsKey: 'notDue' },
  { key: 'd1_30', bucket: 1, label: '0-30 Gün', totalsKey: 'days1To30' },
  { key: 'd31_60', bucket: 2, label: '31-60 Gün', totalsKey: 'days31To60' },
  { key: 'd61_90', bucket: 3, label: '61-90 Gün', totalsKey: 'days61To90' },
  { key: 'd90p', bucket: 4, label: '90+ Gün', totalsKey: 'days90Plus' },
];

export const bucketLabel = (b) => (AGING_BUCKETS.find((x) => x.bucket === Number(b)) || {}).label || '-';

/* ── YAŞLANDIRMA: OData karşılıkları (c360 listesi için) ────────────────────────────────────────
   /api/reports/aging DÜZ JSON döner (OData değil) → sayfalanamaz/sıralanamaz. Yaşlandırma listesi
   bu yüzden Invoice OData'sına bağlanır; kova/gecikme mantığı BURADA, backend'in BucketOf'u ile
   BİREBİR aynı sınırlarla tekrar edilir. Kovaların iki yerde yaşaması bilinçli bir ödünçtür:
   alternatifi (rapor ucuna sayfalama eklemek) BACKEND DEĞİŞİKLİĞİDİR. Sınırlar değişirse İKİSİ
   BİRDEN değişmeli — bu yüzden tek bir yerde (aşağıda) toplandılar. */

/** Yaşlandırmanın konusu: AKTİF satış faturasının AÇIK tutarı (ReportsController.GetAging ile aynı). */
export const AGING_BASE_FILTER = 'Type eq 0 and LifecycleStatus eq 1 and OpenAmount gt 0';

/** Bugünün yerel gece yarısı — backend'in `DateTimeOffset.Now.Date` referansı. */
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const isoDaysAgo = (n) => {
  const d = startOfToday();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};

/** Vadesi geçmiş = vadesi VAR ve bugünden önce (vadesiz fatura gecikmiş SAYILMAZ). */
export const overdueFilter = () => `(DueDate ne null and DueDate lt ${startOfToday().toISOString()})`;

/**
 * Yaş kovası → OData $filter. Sınırlar ReportsController.BucketOf ile birebir:
 *   0 = vadesi gelmemiş VEYA vadesiz · 1 = 1..30 · 2 = 31..60 · 3 = 61..90 · 4 = 90+ gün gecikme.
 */
export function agingBucketFilter(bucket) {
  const today = startOfToday().toISOString();
  switch (Number(bucket)) {
    case 0: return `(DueDate eq null or DueDate ge ${today})`;
    case 1: return `(DueDate lt ${today} and DueDate ge ${isoDaysAgo(30)})`;
    case 2: return `(DueDate lt ${isoDaysAgo(30)} and DueDate ge ${isoDaysAgo(60)})`;
    case 3: return `(DueDate lt ${isoDaysAgo(60)} and DueDate ge ${isoDaysAgo(90)})`;
    case 4: return `(DueDate lt ${isoDaysAgo(90)})`;
    default: return '';
  }
}

/** Gecikme günü (backend: (bugün − Vade.Date).Days, negatifse 0). */
export function daysOverdueOf(dueDate) {
  if (!dueDate) return 0;
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return 0;
  due.setHours(0, 0, 0, 0);
  const diff = Math.floor((startOfToday().getTime() - due.getTime()) / 86400000);
  return diff > 0 ? diff : 0;
}

/** Gecikme günü → kova (ReportsController.BucketOf). */
export function bucketOf(daysOverdue) {
  const d = Number(daysOverdue) || 0;
  if (d <= 0) return 0;
  if (d <= 30) return 1;
  if (d <= 60) return 2;
  if (d <= 90) return 3;
  return 4;
}

// --- Ortak yardımcılar (camel/Pascal serileştirmeye dayanıklı) ---
export function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
export const unwrap = (payload) => pick(payload, 'Data', 'data') ?? payload;

export const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
export const round2 = (v) => Math.round((toNum(v) + Number.EPSILON) * 100) / 100;

export const fmtMoney = (v) =>
  `${toNum(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
export const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('tr-TR') : '-');

/**
 * Bir tahsilatın BİR faturaya bağlayabileceği TAVAN.
 *
 * Backend (InvoiceAllocationService.OpenExcluding) tavanı "bu tahsilat HARİÇ açık tutar" olarak
 * hesaplar. Fatura zaten BU tahsilat tarafından (kısmen) kapatılmışsa persisted OpenAmount o payı
 * DÜŞMÜŞTÜR → UI aynı payı geri eklemezse kendi eşleştirmesini tavana takar ve yeniden eşleştirme
 * imkânsızlaşır.
 *
 *   tavan = OpenAmount + (bu tahsilatın o faturaya HÂLİHAZIRDA bağladığı tutar)
 */
export const effectiveOpen = (openAmount, mine = 0) => round2(toNum(openAmount) + toNum(mine));

async function readJson(res, errText) {
  if (!res.ok) {
    let msg = errText;
    try {
      const body = unwrap(await res.json());
      msg = pick(body, 'message', 'Message') || (typeof body === 'string' ? body : msg);
    } catch { /* gövde okunamadı — varsayılan mesaj */ }
    throw new Error(msg || errText);
  }
  return unwrap(await res.json());
}

/**
 * Müşterinin AÇIK faturaları — ONLINE, ASLA CACHE'LENMEZ (spec §2).
 * Yalnız OpenAmount > 0 döner; kapalı fatura HİÇ GELMEZ (mükerrer tahsilat savunması).
 * Rota: literal segment önde ("open-invoices/{id}") — OData convention routing'e takılmasın diye.
 */
export async function fetchOpenInvoices(customerId, { onlyOverdue = false, q = '' } = {}) {
  const params = new URLSearchParams();
  if (onlyOverdue) params.set('onlyOverdue', 'true');
  if (q) params.set('q', q);
  const qs = params.toString();
  const res = await fetch(`${CUSTOMER_API}/open-invoices/${customerId}${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
    cache: 'no-store',
  });
  const data = await readJson(res, 'Açık faturalar alınamadı');
  return {
    totalOpen: toNum(pick(data, 'totalOpen', 'TotalOpen')),
    overdueOpen: toNum(pick(data, 'overdueOpen', 'OverdueOpen')),
    oldestOpenDays: toNum(pick(data, 'oldestOpenDays', 'OldestOpenDays')),
    count: toNum(pick(data, 'count', 'Count')),
    invoices: (pick(data, 'invoices', 'Invoices') || []).map(normalizeOpenInvoice),
  };
}

// open-invoices satırı → tek biçim.
export function normalizeOpenInvoice(i) {
  return {
    id: pick(i, 'id', 'Id'),
    invoiceNo: pick(i, 'invoiceNo', 'InvoiceNo') || '-',
    issuedAt: pick(i, 'issuedAt', 'IssuedAt'),
    dueDate: pick(i, 'dueDate', 'DueDate'),
    netAmount: toNum(pick(i, 'netAmount', 'NetAmount')),
    openAmount: toNum(pick(i, 'openAmount', 'OpenAmount')),
    invoiceStatus: toNum(pick(i, 'invoiceStatus', 'InvoiceStatus')),
    daysOverdue: toNum(pick(i, 'daysOverdue', 'DaysOverdue')),
    source: pick(i, 'source', 'Source') || 'Backoffice',
  };
}

/** Tahsilat + ilişkili faturaları (collectionAllocations, Invoice snapshot'ı dahil). */
export async function fetchTransaction(id) {
  const res = await fetch(`${FT_API}/GetById?Id=${id}`, { headers: authHeaders() });
  return readJson(res, 'Tahsilat alınamadı');
}

/** Fatura + ilişkili tahsilatlar (collectionAllocations) + iade faturaları (returnInvoices). */
export async function fetchInvoiceWithAllocations(id) {
  const res = await fetch(`${INVOICE_API}/GetById?Id=${id}`, { headers: authHeaders() });
  return readJson(res, 'Fatura alınamadı');
}

/** CollectionAllocation satırı → tek biçim (Invoice snapshot'ı varsa taşınır). */
export function normalizeAllocation(a) {
  const inv = pick(a, 'invoice', 'Invoice');
  return {
    id: pick(a, 'id', 'Id'),
    invoiceId: pick(a, 'invoiceId', 'InvoiceId'),
    financeTransactionId: pick(a, 'financeTransactionId', 'FinanceTransactionId') || null,
    paymentId: pick(a, 'paymentId', 'PaymentId') || null,
    allocatedAmount: toNum(pick(a, 'allocatedAmount', 'AllocatedAmount')),
    allocatedBy: toNum(pick(a, 'allocatedBy', 'AllocatedBy')),
    allocatedAt: pick(a, 'allocatedAt', 'AllocatedAt'),
    note: pick(a, 'note', 'Note') || '',
    invoice: inv
      ? {
        id: pick(inv, 'id', 'Id'),
        invoiceNo: pick(inv, 'textId', 'TextId') || pick(inv, 'printedNo', 'PrintedNo') || '-',
        issuedAt: pick(inv, 'documentDate', 'DocumentDate'),
        dueDate: pick(inv, 'dueDate', 'DueDate'),
        netAmount: toNum(pick(inv, 'netAmount', 'NetAmount')),
        openAmount: toNum(pick(inv, 'openAmount', 'OpenAmount')),
        invoiceStatus: toNum(pick(inv, 'invoiceStatus', 'InvoiceStatus')),
      }
      : null,
  };
}

/** allocations: [{ invoiceId, amount }] — BOŞ liste = tümü avans (Avans Bırak). */
export async function postAllocate(id, allocations) {
  const res = await fetch(`${FT_API}/${id}/allocate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ allocations }),
  });
  return readJson(res, 'Eşleştirme kaydedilemedi');
}

/** Backoffice düzeltmesi — GEREKÇE ZORUNLU (backend de reddeder). */
export async function postReallocate(id, allocations, reason) {
  const res = await fetch(`${FT_API}/${id}/reallocate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ allocations, reason }),
  });
  return readJson(res, 'Yeniden eşleştirme kaydedilemedi');
}

/** Yaşlandırma — açık faturaların vade yaşına göre kovalanması. */
export async function fetchAging({ customerId, representativeId } = {}) {
  const params = new URLSearchParams();
  if (customerId) params.set('customerId', customerId);
  if (representativeId) params.set('representativeId', representativeId);
  const qs = params.toString();
  const res = await fetch(`${AGING_API}${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
  return readJson(res, 'Yaşlandırma raporu alınamadı');
}

/**
 * VERİ HATASI kuyruğu — açık tutarı NEGATİF (overpaid) faturalar.
 * Aging ve open-invoices uçları OpenAmount > 0 filtrelediğinden bunlar oralarda GÖRÜNMEZ;
 * OData ile ayrıca çekilir ve uyarı listesinde kırmızı gösterilir (spec §10.10).
 */
export async function fetchOverpaidInvoices() {
  const filter = 'Type eq 0 and LifecycleStatus eq 1 and OpenAmount lt 0';
  const url = `${INVOICE_API}?$filter=${encodeURIComponent(filter)}&$top=50&$orderby=OpenAmount asc`;
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) return [];
  const data = await res.json();
  const rows = Array.isArray(data) ? data : (data?.value || []);
  return rows.map((i) => ({
    id: pick(i, 'id', 'Id'),
    invoiceNo: pick(i, 'textId', 'TextId') || '-',
    customerId: pick(i, 'customerId', 'CustomerId'),
    netAmount: toNum(pick(i, 'netAmount', 'NetAmount')),
    openAmount: toNum(pick(i, 'openAmount', 'OpenAmount')),
    allocatedAmount: toNum(pick(i, 'allocatedAmount', 'AllocatedAmount')),
    returnedAmount: toNum(pick(i, 'returnedAmount', 'ReturnedAmount')),
  }));
}

/** Lookup satırı → "KOD — Ad" etiketi (FinanceTransactionsPage ile aynı). */
export const lookupLabelOf = (row, nameField) => {
  const code = pick(row, 'TextId', 'textId') || '';
  const name = row[nameField] || row[nameField.toLowerCase()] || row.Name || row.name || '';
  return [code, name].filter(Boolean).join(' — ') || code || '(adsız)';
};

/** Verilen id'lerin adlarını topluca çöz → { id: etiket }. */
export async function resolveNames(api, ids, nameField) {
  const unique = Array.from(new Set((ids || []).filter(Boolean)));
  if (unique.length === 0) return {};
  const filter = unique.map((id) => `Id eq ${id}`).join(' or ');
  const url = `${api}?$filter=${encodeURIComponent(filter)}&$top=${unique.length}`;
  try {
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) return {};
    const data = await res.json();
    const rows = Array.isArray(data) ? data : (data?.value || []);
    const map = {};
    rows.forEach((r) => { map[pick(r, 'id', 'Id')] = lookupLabelOf(r, nameField); });
    return map;
  } catch {
    return {};
  }
}
